import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  Section,
  SectionItem,
  SectionHeader,
  FeatureCard,
  TestimonialCard,
  FeatureShowcase,
  SEO,
  PhoneMockup,
} from '@/components';
import { trackCTAClick, trackConversion } from '@/utils/analytics';
import { features, featureCategories, getFeatureById } from '@/data/features';
import { getFeaturedTestimonials } from '@/data/testimonials';
import { companyStats, integrations } from '@/data/navigation';

// Hand-pick the best features to showcase on the homepage
const showcaseFeatureIds = ['scheduling', 'voip', 'dashboards', 'profitability'];
const heroFeatures = showcaseFeatureIds.map(id => getFeatureById(id)).filter(Boolean) as typeof features;
const testimonials = getFeaturedTestimonials(3);

export function HomePage() {
  return (
    <>
      <SEO
        title="Omniforge - Forge a Business That Lasts"
        description="The complete operating system for service companies. Scheduling to strategy, hiring to training, bids to collections—all in one platform. HVAC, plumbing, electrical, landscaping, and more."
        keywords={[
          'field service management',
          'service company software',
          'HVAC software',
          'plumbing software',
          'electrical contractor software',
          'dispatch software',
          'scheduling software',
          'field service app',
        ]}
        ogImage="/og/home.png"
        type="website"
      />

      {/* Hero Section */}
      <section className="relative bg-ink overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-ink via-ink to-charcoal" />
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-accent/5 blur-3xl rounded-full" />

        <div className="container-marketing relative z-10 pt-28 pb-16 md:pt-36 md:pb-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Hero Text */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            >
              <h1 className="font-display font-bold text-hero text-paper mb-6 not-italic">
                Forge a business
                <br />
                <span className="text-accent">that lasts.</span>
              </h1>
              <p className="text-lg text-gray-400 mb-4">
                The complete operating system for service companies
              </p>
              <p className="text-xl text-gray-300 mb-8 max-w-lg">
                Scheduling to strategy. Hiring to training. Bids to collections.
                Everything you need to run your business—in one platform.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-8">
                <Link
                  to="/demo"
                  className="btn-primary text-lg px-8 py-4"
                  onClick={() => trackCTAClick('hero_request_demo', '/demo')}
                >
                  Request Demo
                </Link>
                <Link
                  to="/pricing"
                  className="inline-flex items-center justify-center px-8 py-4 text-lg font-semibold text-white border-2 border-white rounded-lg hover:bg-white hover:text-ink transition-all"
                  onClick={() => trackCTAClick('hero_view_pricing', '/pricing')}
                >
                  View Pricing
                </Link>
              </div>

              {/* Social proof */}
              <div className="flex items-center gap-4">
                <div className="flex -space-x-2">
                  {[
                    'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=80&h=80&fit=crop&crop=face',
                    'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=80&h=80&fit=crop&crop=face',
                    'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=80&h=80&fit=crop&crop=face',
                    'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=80&h=80&fit=crop&crop=face',
                    'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=80&h=80&fit=crop&crop=face',
                  ].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt=""
                      width={40}
                      height={40}
                      loading="lazy"
                      decoding="async"
                      className="w-10 h-10 rounded-full border-2 border-ink object-cover"
                    />
                  ))}
                </div>
                <div className="text-gray-300">
                  <div className="flex items-center gap-1 text-yellow-400">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <StarIcon key={i} className="w-4 h-4" />
                    ))}
                  </div>
                  <p className="text-sm">&ldquo;Finally, software that actually works&rdquo;</p>
                </div>
              </div>
            </motion.div>

            {/* Mobile App Preview */}
            {/* TODO: Replace placeholder UI with real mobile app screenshots */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="relative flex justify-center"
            >
              <PhoneMockup />
              {/* Floating notification */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 }}
                className="absolute top-1/4 -left-4 md:-left-8 bg-white rounded-xl p-3 md:p-4 shadow-elevated max-w-[160px] md:max-w-none"
              >
                <div className="flex items-center gap-2 md:gap-3">
                  <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-success-light flex items-center justify-center flex-shrink-0">
                    <CheckIcon className="w-4 h-4 md:w-5 md:h-5 text-success" />
                  </div>
                  <div>
                    <p className="font-semibold text-ink text-sm md:text-base">Job Completed</p>
                    <p className="text-xs md:text-sm text-muted">$2,450 received</p>
                  </div>
                </div>
              </motion.div>
              {/* Floating stat */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8 }}
                className="absolute top-1/2 -right-4 md:-right-8 bg-white rounded-xl p-3 md:p-4 shadow-elevated"
              >
                <div className="text-center">
                  <p className="text-xl md:text-2xl font-bold text-accent">+32%</p>
                  <p className="text-[10px] md:text-xs text-muted">Revenue this month</p>
                </div>
              </motion.div>
              {/* Floating tech count */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.0 }}
                className="absolute bottom-8 -right-2 md:-right-4 bg-white rounded-xl p-3 md:p-4 shadow-elevated"
              >
                <div className="flex items-center gap-2">
                  <div className="flex -space-x-2">
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-accent text-white flex items-center justify-center text-xs font-bold">M</div>
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-success text-white flex items-center justify-center text-xs font-bold">J</div>
                    <div className="w-6 h-6 md:w-8 md:h-8 rounded-full bg-warning text-white flex items-center justify-center text-xs font-bold">S</div>
                  </div>
                  <p className="text-xs md:text-sm text-muted">5 techs active</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

{/* Trust Bar */}
      {/* TODO: Replace placeholder company names and logos with real customers when available */}
      <Section background="cream" className="py-8 md:py-12" animate={false}>
        <div className="text-center mb-6">
          <p className="text-muted">Trusted by 500+ service companies</p>
        </div>
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 opacity-60">
          {['Richardson HVAC', 'Elite Plumbing', 'Spark Electric', 'Williams Landscaping', 'Crystal Clear'].map(
            (name) => (
              <div key={name} className="text-lg font-semibold text-muted">
                {name}
              </div>
            )
          )}
        </div>
      </Section>

      {/* Pain Points */}
      <Section>
        <SectionHeader
          title="Running a service company shouldn't feel like this."
          subtitle="Most owners are trapped in chaos. There's a better way."
        />
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {painPoints.map((point, i) => (
            <SectionItem key={i}>
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl bg-accent-light mx-auto mb-4 flex items-center justify-center">
                  <point.icon className="w-8 h-8 text-accent" />
                </div>
                <h3 className="font-heading font-semibold text-lg mb-2">{point.title}</h3>
                <p className="text-muted">{point.description}</p>
              </div>
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Solution / Feature Showcase */}
      <Section background="cream">
        <SectionHeader
          title="See everything. Control everything."
          subtitle="Visual management dashboards, real-time operations, and industry-specific workflows—all working together."
        />
        <FeatureShowcase features={heroFeatures} />
      </Section>

      {/* Full Platform Overview */}
      <Section>
        <SectionHeader
          title="A complete operating system. Not just software."
          subtitle={`${features.length}+ integrated modules across 7 core systems—all sharing data, all driving visibility.`}
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {featureCategories.map((category) => (
            <SectionItem key={category.id}>
              <div className="card h-full hover:border-accent border border-transparent transition-all">
                <h3 className="font-heading font-semibold text-lg mb-2">{category.name}</h3>
                <p className="text-sm text-muted mb-3">{category.description}</p>
                <ul className="space-y-1">
                  {category.features.slice(0, 3).map((f) => (
                    <li key={f.id} className="text-xs text-muted flex items-center gap-1.5">
                      <span className="w-1 h-1 rounded-full bg-accent" />
                      {f.name}
                    </li>
                  ))}
                  {category.features.length > 3 && (
                    <li className="text-xs text-accent">+{category.features.length - 3} more</li>
                  )}
                </ul>
              </div>
            </SectionItem>
          ))}
        </div>
        <div className="text-center">
          <Link to="/features" className="btn-secondary">
            Explore All {features.length}+ Features
          </Link>
        </div>
      </Section>

      {/* Why We're Different */}
      <Section>
        <SectionHeader
          title="This is different. Here's why."
          subtitle="Other software gives you tools. We give you transformation."
        />
        <div className="grid md:grid-cols-3 gap-8">
          {differentiators.map((item, i) => (
            <SectionItem key={i}>
              <FeatureCard
                icon={<item.icon className="w-6 h-6" />}
                title={item.title}
                description={item.description}
              />
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Industry Solutions */}
      <Section background="cream">
        <SectionHeader
          title="Purpose-built for your trade"
          subtitle="Commercial or residential. Emergency service or recurring routes. Omniforge is configured for how your specific industry works."
        />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          {industries.map((industry, i) => (
            <SectionItem key={i}>
              <Link
                to={industry.href}
                className="block p-4 rounded-xl bg-white hover:bg-accent-light border border-gray-100 hover:border-accent transition-all group text-center"
              >
                <div className="w-10 h-10 rounded-lg bg-accent-light mx-auto mb-2 flex items-center justify-center group-hover:bg-accent transition-colors">
                  <industry.icon className="w-5 h-5 text-accent group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-heading font-semibold text-sm">{industry.name}</h3>
              </Link>
            </SectionItem>
          ))}
        </div>
        <div className="text-center mt-8">
          <p className="text-muted mb-4">
            Plus: Irrigation, Janitorial, Septic & Drain, Fencing, Flooring, Chimney, Solar, Security Systems, and more.
          </p>
          <p className="text-muted">
            Don't see your industry?{' '}
            <Link to="/contact" className="link-accent">
              Let's talk—we'll build it for you.
            </Link>
          </p>
        </div>
      </Section>

      {/* Testimonials */}
      <Section>
        <SectionHeader
          title="What our customers say"
          subtitle="Real results from real service companies"
        />
        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((testimonial) => (
            <SectionItem key={testimonial.id}>
              <TestimonialCard
                quote={testimonial.quote}
                author={testimonial.author}
                role={testimonial.role}
                company={testimonial.company}
                imageUrl={testimonial.imageUrl}
                stat={testimonial.stats}
              />
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Stats Section */}
      <Section background="accent">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {companyStats.map((stat, i) => (
            <SectionItem key={i}>
              <div className="text-center">
                <motion.div
                  className="text-5xl md:text-6xl font-bold text-white mb-2"
                  initial={{ opacity: 0, scale: 0.5 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1, duration: 0.5 }}
                >
                  {stat.value}
                  {stat.suffix}
                </motion.div>
                <div className="text-white/80">{stat.label}</div>
              </div>
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Business Rhythm Section */}
      <Section background="cream">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <SectionItem>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-light text-accent text-sm font-medium mb-4">
              <TargetIcon className="w-4 h-4" />
              Business Rhythm Built In
            </div>
            <h2 className="font-display text-h2 mb-4">Create a rhythm that drives results.</h2>
            <p className="text-muted mb-6">
              Weekly meetings, quarterly goals, annual planning, and team accountability—all built into your daily operations.
              Stop running your business by the seat of your pants.
            </p>
            <ul className="space-y-3 mb-8">
              {rhythmFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-3">
                  <CheckIcon className="w-5 h-5 text-success" />
                  <span>{feature}</span>
                </li>
              ))}
            </ul>
            <Link to="/features#business-rhythm" className="link-accent inline-flex items-center gap-2">
              Explore Business Rhythm
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </SectionItem>
          <SectionItem>
            <div className="aspect-video rounded-2xl shadow-elevated overflow-hidden">
              <img
                src="/images/screenshots/eos-vto.png"
                alt="Omniforge V/TO - Vision and Traction Organizer for business rhythm"
                width={800}
                height={450}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
              />
            </div>
          </SectionItem>
        </div>
      </Section>

      {/* Integrations */}
      <Section>
        <SectionHeader
          title="Works with the tools you already use"
          subtitle="Seamless integrations with your accounting, payments, and communication tools."
        />
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12">
          {integrations.map((integration) => (
            <SectionItem key={integration.id}>
              <div className="w-24 h-24 rounded-2xl bg-cream flex items-center justify-center">
                <span className="font-medium text-muted text-sm text-center">
                  {integration.name}
                </span>
              </div>
            </SectionItem>
          ))}
        </div>
      </Section>

      {/* Final CTA */}
      <Section background="ink">
        <div className="text-center max-w-3xl mx-auto">
          <SectionItem>
            <h2 className="font-display text-h2 text-paper mb-4">
              Ready to transform how you run your business?
            </h2>
            <p className="text-gray-300 mb-8">
              See the operating system in action. We'll show you exactly how Omniforge can change everything.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/demo"
                className="btn-primary text-lg px-8 py-4"
                onClick={() => trackCTAClick('footer_request_demo', '/demo')}
              >
                Request Demo
              </Link>
{/* TODO: Replace with real phone number when available */}
              <a
                href="tel:+15551234567"
                className="btn-secondary border-white text-white hover:bg-white hover:text-ink text-lg px-8 py-4"
                onClick={() => {
                  trackCTAClick('footer_phone_call', 'tel:+15551234567');
                  trackConversion('phone_call_click');
                }}
              >
                Call (555) 123-4567
              </a>
            </div>
          </SectionItem>
        </div>
      </Section>
    </>
  );
}

// Data
const painPoints = [
  {
    icon: ChartIcon,
    title: 'Flying blind',
    description: "You don't know which jobs make money, which techs perform, or where your business actually stands—until it's too late.",
  },
  {
    icon: AppsIcon,
    title: 'Duct-taped systems',
    description: 'Scheduling here, invoicing there, spreadsheets everywhere. No single source of truth. No real visibility.',
  },
  {
    icon: MissedIcon,
    title: 'Reactive, not proactive',
    description: "Every day is firefighting. Problems find you instead of you finding them. Growth feels impossible.",
  },
  {
    icon: ClockIcon,
    title: 'No operating rhythm',
    description: "Meetings accomplish nothing. Goals get forgotten. Accountability is a word, not a practice.",
  },
];

const differentiators = [
  {
    icon: ImplementIcon,
    title: 'We Implement, You Succeed',
    description:
      "This isn't software you figure out alone. We install the operating system in your business—processes, training, and all.",
  },
  {
    icon: SuccessIcon,
    title: 'Transformation, Not Just Tools',
    description:
      'Dedicated success team that drives adoption, measures results, and ensures you actually achieve the transformation.',
  },
  {
    icon: TradesIcon,
    title: 'Built By Operators, For Operators',
    description:
      'Created by people who ran service companies and felt the pain. Every feature exists because we needed it.',
  },
];

const industries = [
  // Core Trades
  { name: 'HVAC', tagline: 'Equipment tracking, maintenance agreements', href: '/industries/hvac', icon: HvacIcon },
  { name: 'Plumbing', tagline: 'Emergency dispatch, permits', href: '/industries/plumbing', icon: PlumbingIcon },
  { name: 'Electrical', tagline: 'Project tracking, inspections', href: '/industries/electrical', icon: ElectricalIcon },
  { name: 'Roofing', tagline: 'Storm tracking, crew scheduling', href: '/industries/roofing', icon: RoofingIcon },
  // Property Services
  { name: 'Landscaping', tagline: 'Route optimization, seasonal contracts', href: '/industries/landscaping', icon: LandscapingIcon },
  { name: 'Lawn Care', tagline: 'Recurring routes, property management', href: '/industries/lawn-care', icon: LandscapingIcon },
  { name: 'Tree Service', tagline: 'Crew scheduling, equipment tracking', href: '/industries/tree-service', icon: LandscapingIcon },
  // Cleaning & Maintenance
  { name: 'Window Cleaning', tagline: 'Route optimization, recurring schedules', href: '/industries/window-cleaning', icon: GlaziersIcon },
  { name: 'Pressure Washing', tagline: 'Quote builder, before/after photos', href: '/industries/pressure-washing', icon: CleaningIcon },
  { name: 'Carpet Cleaning', tagline: 'Room-based pricing, upsells', href: '/industries/carpet-cleaning', icon: CleaningIcon },
  // Specialty Trades
  { name: 'Garage Doors', tagline: 'Parts inventory, emergency dispatch', href: '/industries/garage-doors', icon: HandymanIcon },
  { name: 'Painting', tagline: 'Estimating, crew management', href: '/industries/painting', icon: HandymanIcon },
  // Home Services
  { name: 'Pest Control', tagline: 'Recurring routes, compliance', href: '/industries/pest-control', icon: PestControlIcon },
  { name: 'Pool Service', tagline: 'Chemical tracking, maintenance routes', href: '/industries/pool-service', icon: PlumbingIcon },
  { name: 'Appliance Repair', tagline: 'Parts ordering, warranty tracking', href: '/industries/appliance-repair', icon: HvacIcon },
  { name: 'Handyman', tagline: 'Multi-trade, flat-rate pricing', href: '/industries/handyman', icon: HandymanIcon },
];

const rhythmFeatures = [
  'Structured weekly meeting runner',
  'Vision & strategy planning tools',
  'Quarterly goal tracking with accountability',
  'Scorecard dashboards with automated KPIs',
  'Issues list with solve workflow',
  'Org chart & accountability',
  'Team health assessments',
  'Core process documentation',
];

// Icons
function StarIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </svg>
  );
}

function ArrowRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
    </svg>
  );
}

function TargetIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function AppsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
    </svg>
  );
}

function MissedIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
    </svg>
  );
}

function ClockIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function ChartIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  );
}

function ImplementIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  );
}

function SuccessIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function TradesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  );
}

function HvacIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  );
}

function PlumbingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
    </svg>
  );
}

function ElectricalIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  );
}

function LandscapingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function GlaziersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4h16v16H4V4zM4 12h16M12 4v16" />
    </svg>
  );
}

function PestControlIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 0V4m-4 8H4m4 4l-2.83 2.83M12 16v4m4-4l2.83 2.83M20 12h-4m-1.17-5.17L12 4" />
    </svg>
  );
}

function HandymanIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" />
    </svg>
  );
}

function RoofingIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l9-9 9 9M5 10v10a1 1 0 001 1h3a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1h3a1 1 0 001-1V10" />
    </svg>
  );
}

function CleaningIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  );
}
